import { t } from "@travily/i18n";
import { Dialog, DialogTitle } from "@ui/components/Dialog";
import { SectionTab, SectionTabList, SectionTabPanel, SectionTabs } from "@ui/components/SectionTabs";
import { findLocale, SUPPORTED_CURRENCIES, SUPPORTED_LOCALES } from "@ui/lib/locale-data";
import { tv } from "@ui/lib/tv";
import { CurrencyGrid } from "./components/CurrencyGrid";
import { LocaleGrid } from "./components/LocaleGrid";

export type LocaleModalTab = "language" | "currency";

const localeModal = tv({
  slots: {
    header: [
      "flex shrink-0 items-start justify-between border-b border-neutral-100",
      "px-5 md:px-0",
      "max-md:pt-2 md:pt-5",
    ],
  },
})();

export interface LocaleModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  currentLocale: string;
  currentCurrency: string;
  suggestedLocales?: string[];
  defaultTab?: LocaleModalTab;
  onLocaleSelect: (locale: string) => void;
  onCurrencySelect: (currency: string) => void;
}

export function LocaleModal({
  isOpen,
  onOpenChange,
  currentLocale,
  currentCurrency,
  suggestedLocales = [],
  defaultTab = "language",
  onLocaleSelect,
  onCurrencySelect,
}: LocaleModalProps) {
  const suggested = suggestedLocales
    .map((code) => findLocale(code))
    .filter((l): l is NonNullable<typeof l> => l != null);

  const suggestedCodes = new Set(suggested.map((l) => l.code));
  const remainingLocales = SUPPORTED_LOCALES.filter((l) => !suggestedCodes.has(l.code));

  return (
    <Dialog.Root isOpen={isOpen} onOpenChange={onOpenChange} size="lg">
      <Dialog.Content>
        <DialogTitle className="sr-only">{t("components.views.shared.locale_modal.title")}</DialogTitle>

        <SectionTabs defaultSelectedKey={defaultTab} size="header" className="flex flex-1 flex-col overflow-hidden">
          {/* Tab list with close button — compact at the sheet's top edge on mobile, header-sized on desktop */}
          <div className={localeModal.header()}>
            <SectionTabList>
              <SectionTab id="language">{t("components.views.shared.locale_modal.tab_language")}</SectionTab>
              <SectionTab id="currency">{t("components.views.shared.locale_modal.tab_currency")}</SectionTab>
            </SectionTabList>
            {/* Visible on every breakpoint: the mobile sheet's focusable dismiss path. */}
            <Dialog.CloseButton />
          </div>

          <Dialog.Body className="pt-0">
            <SectionTabPanel id="language">
              <div className="flex flex-col gap-8 py-6">
                {suggested.length > 0 && (
                  <section>
                    <h3 className="mb-4 text-lg font-bold text-neutral-900">
                      {t("components.views.shared.locale_modal.suggested_heading")}
                    </h3>
                    <LocaleGrid
                      ariaLabel={t("components.views.shared.locale_modal.suggested_heading")}
                      locales={suggested}
                      currentLocale={currentLocale}
                      onSelect={onLocaleSelect}
                    />
                  </section>
                )}

                <section>
                  <h3 className="mb-4 text-lg font-bold text-neutral-900">
                    {t("components.views.shared.locale_modal.choose_language_heading")}
                  </h3>
                  <LocaleGrid
                    ariaLabel={t("components.views.shared.locale_modal.choose_language_heading")}
                    locales={suggested.length > 0 ? remainingLocales : SUPPORTED_LOCALES}
                    currentLocale={currentLocale}
                    onSelect={onLocaleSelect}
                  />
                </section>
              </div>
            </SectionTabPanel>

            <SectionTabPanel id="currency">
              <div className="flex flex-col gap-8 py-6">
                <section>
                  <h3 className="mb-4 text-lg font-bold text-neutral-900">
                    {t("components.views.shared.locale_modal.choose_currency_heading")}
                  </h3>
                  <CurrencyGrid
                    ariaLabel={t("components.views.shared.locale_modal.choose_currency_heading")}
                    currencies={SUPPORTED_CURRENCIES}
                    currentCurrency={currentCurrency}
                    onSelect={onCurrencySelect}
                  />
                  <p className="mt-4 text-xs text-neutral-400">
                    {t("components.views.shared.locale_modal.price_disclaimer")}
                  </p>
                </section>
              </div>
            </SectionTabPanel>
          </Dialog.Body>
        </SectionTabs>
      </Dialog.Content>
    </Dialog.Root>
  );
}
